import { modelOptions, turbineTypeOptions } from './utils';
import styles from '../styles.module.css';
import { AdminCardForm } from '@/components/AdminToolComponents/card';
import Input from '@/components/Form/Input';
import RadioButton from '@/components/Form/RadioButton';
import Select from '@/components/Form/Select';
import { TurbineFormData } from '@/hooks/services/Turbines';
import { TURBINE_IS_ACTIVE } from '@/solaris-lens-common-lib/src/constants/turbines';

export const statusContainer = ({
  showEditTurbine = true,
  formData,
  handleChangeFormData = () => {}
}: {
  showEditTurbine?: boolean;
  formData: TurbineFormData;
  handleChangeFormData: (field: keyof TurbineFormData, value: any) => void;
}) => {
  const radioOptions = {
    active: TURBINE_IS_ACTIVE.ACTIVE,
    inactive: TURBINE_IS_ACTIVE.INACTIVE
  };

  return showEditTurbine ? (
    <>
      <RadioButton
        checked={formData.isActive}
        onChange={() => {
          handleChangeFormData('isActive', true);
          handleChangeFormData('status', radioOptions.active);
        }}
        label={radioOptions.active}
      />
      <RadioButton
        checked={!formData.isActive}
        onChange={() => {
          handleChangeFormData('isActive', false);
          handleChangeFormData('status', radioOptions.inactive);
        }}
        label={radioOptions.inactive}
      />
    </>
  ) : (
    <>
      <span className={styles.adminText}>Status</span>
      <span className={styles.adminBlueText}>
        {formData.isActive ? TURBINE_IS_ACTIVE.ACTIVE : TURBINE_IS_ACTIVE.INACTIVE}
      </span>
    </>
  );
};

export const TurbineForm = ({
  formData,
  handleChangeFormData,
  showEditTurbine = true,
  isNewTurbine = false,
  formErrors = {}
}: {
  formData: TurbineFormData;
  handleChangeFormData: (field: keyof TurbineFormData, value: any) => void;
  showEditTurbine?: boolean;
  isNewTurbine?: boolean;
  formErrors?: { [k: string]: string };
}) => {
  const requiredFields = isNewTurbine
    ? ['serialNumber', 'name', 'brand']
    : ['name', 'brand'];

  const inputComponent = ({
    id,
    label,
    extraProps,
    readOnly = false
  }: {
    id: keyof TurbineFormData;
    label: string;
    extraProps?: any;
    readOnly?: boolean;
  }) => (
    <Input
      label={label}
      value={formData[id] as string}
      onChange={(value: string) => handleChangeFormData(id, value)}
      required={requiredFields.includes(id as string)}
      readOnly={readOnly}
      error={!!formErrors[id as string]}
      customClass={styles.adminInput}
      {...extraProps}
    />
  );

  const selectComponent = ({
    id,
    label,
    options,
    extraProps
  }: {
    id: keyof TurbineFormData;
    label: string;
    options: { value: string; label: string }[];
    extraProps?: any;
  }) => (
    <Select
      label={label}
      value={formData[id] as string}
      onChange={(value: string) => handleChangeFormData(id, value)}
      options={options}
      required={requiredFields.includes(id as string)}
      readOnly={!showEditTurbine}
      customClass={styles.adminInput}
      {...extraProps}
    />
  );

  return (
    <AdminCardForm>
      {inputComponent({
        id: isNewTurbine ? 'serialNumber' : 'id',
        label: 'Serial Number',
        readOnly: !isNewTurbine
      })}

      {inputComponent({
        id: 'maxCapacity',
        label: 'Max Capacity MW',
        readOnly: !showEditTurbine
      })}

      {inputComponent({
        id: 'name',
        label: 'Name',
        readOnly: !showEditTurbine
      })}

      {inputComponent({
        id: 'manufacturingCountry',
        label: 'Manufacturing Country',
        readOnly: !showEditTurbine
      })}

      {inputComponent({
        id: 'brand',
        label: 'Brand',
        readOnly: !showEditTurbine
      })}

      {selectComponent({
        id: 'manufacturingYear',
        label: 'Manufacturing Year',
        options: Array.from({ length: 30 }, (_, i) => {
          const year = new Date().getFullYear() - i;
          return { value: year.toString(), label: year.toString() };
        })
      })}

      {selectComponent({
        id: 'model',
        label: 'Model',
        options: modelOptions.map(model => ({ value: model, label: model }))
      })}

      {selectComponent({
        id: 'acquisitionYear',
        label: 'Acquisition Year',
        options: Array.from({ length: 30 }, (_, i) => {
          const year = new Date().getFullYear() - i;
          return { value: year.toString(), label: year.toString() };
        })
      })}

      {selectComponent({
        id: 'turbineType',
        label: 'Turbine Type',
        options: turbineTypeOptions.map(type => ({ value: type, label: type }))
      })}
    </AdminCardForm>
  );
};
